import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';

import { LocationNodeData } from '../../../@types';
import { ParameterDataFormParameterType, ParameterData } from '../../../@types/form-builder.type';
import { useSearchAllocationNodes } from '../../../queries/live-search-query';
import { displayNodeTypeName } from '../../../utils/node-type';

/**
 * What this field holds depends on the parameter type it was configured with: an
 * allocation node for Primary/Secondary Allocation, and a datetime-local string
 * for Scheduled Date. Every other parameter type is set by the backend and takes
 * no input at all.
 */
export type InteractionParameterValue = LocationNodeData | string;

interface InteractionParameterFieldProps {
  preSelectedData: ParameterData;
  value?: InteractionParameterValue;
  onChange: (value: InteractionParameterValue | undefined) => void;
  isRequired?: boolean;
}

export const InteractionParameterField: React.FC<InteractionParameterFieldProps> = ({
  preSelectedData,
  value,
  onChange,
  isRequired,
}) => {
  const parameterType = preSelectedData?.parameterType;

  // Allocation search
  if (
    parameterType === ParameterDataFormParameterType.PrimaryAllocation ||
    parameterType === ParameterDataFormParameterType.SecondaryAllocation
  ) {
    return (
      <AllocationSearchField
        label={parameterType}
        value={typeof value === 'object' ? value : undefined}
        // Autocomplete clears to null; the field's own contract uses undefined.
        onChange={(next) => onChange(next ?? undefined)}
        isRequired={isRequired}
        preSelectedData={preSelectedData}
      />
    );
  }

  // Scheduled Date
  if (parameterType === ParameterDataFormParameterType.ScheduledDate) {
    return (
      <TextField
        fullWidth
        size="small"
        type="datetime-local"
        label="Scheduled Date/Time"
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        required={isRequired}
        InputLabelProps={{ shrink: true }}
      />
    );
  }

  // Default: show parameter type as info
  return (
    <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontStyle: 'italic' }}>
      {parameterType}: Will be set automatically
    </Typography>
  );
};

interface AllocationSearchFieldProps {
  label: string;
  value?: LocationNodeData;
  // MUI Autocomplete reports null when the selection is cleared.
  onChange: (value: LocationNodeData | null) => void;
  isRequired?: boolean;
  preSelectedData: ParameterData;
}

const AllocationSearchField: React.FC<AllocationSearchFieldProps> = ({
  label,
  value,
  onChange,
  isRequired,
  preSelectedData,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<LocationNodeData[]>([]);
  const [loading, setLoading] = useState(false);

  const searchAllocationNodes = useSearchAllocationNodes();

  // Sync inputValue with value prop when parent pre-populates the field
  // Note: inputValue is intentionally NOT in deps - adding it would cause the effect
  // to override user typing when they search. We only want to sync when the parent
  // value changes, not when the user is actively typing in the search field.
  useEffect(() => {
    if (value?.name && inputValue !== value.name) {
      setInputValue(value.name);
    }
    // `inputValue` is deliberately absent: including it would re-sync mid-typing and
    // overwrite what the user is searching for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.name]);

  // Pre-populate with presetSuggestion if available and no value set yet
  // Note: Intentionally limited deps - only trigger on presetSuggestion changes,
  // not on onChange/value changes which would cause infinite loops
  useEffect(() => {
    if (preSelectedData?.presetSuggestion1?.name && !value) {
      searchAllocationNodes
        .mutateAsync(preSelectedData.presetSuggestion1.name)
        .then((results) => {
          // Compare as strings. `LocationNodeData.code` is a string while the
          // template's `presetSuggestion1.code` arrives as a number (which is why
          // prePopulateAllocationFields in utils/mobile-interactions.ts converts it
          // too). Comparing them raw is never equal, so a configured preset would
          // silently fail to prefill.
          const suggestedCode = String(preSelectedData.presetSuggestion1?.code);
          const match = results.find((r) => String(r.code) === suggestedCode);
          if (match) {
            onChange(match);
            setInputValue(match.name);
          }
        })
        .catch(() => {
          // Silently handle search errors during prefill
        });
    }
    // Keyed on the preset only. `value`, `onChange` and the mutation object are
    // omitted on purpose: the effect writes `value` via `onChange`, so depending on
    // either would loop, and the mutation object changes identity every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedData?.presetSuggestion1?.name, preSelectedData?.presetSuggestion1?.code]);

  const handleSearch = async (searchText: string): Promise<void> => {
    if (!searchText || searchText.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const results = await searchAllocationNodes.mutateAsync(searchText);
      setOptions(results || []);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={options}
      loading={loading}
      value={value || null}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        setInputValue(newInputValue);
        // Only search on real typing. MUI also fires this with reason 'reset' when
        // the field is populated (mount with an existing answer, or after a
        // selection), and searching then costs a live-search request per mount just
        // to display a node we already have.
        if (reason === 'input') handleSearch(newInputValue);
      }}
      onChange={(_, newValue) => {
        onChange(newValue);
      }}
      getOptionLabel={(option) => option?.name || ''}
      isOptionEqualToValue={(option, val) => option?.code === val?.code}
      renderOption={(props, option) => (
        <li {...props} key={option.code}>
          <Box>
            <Box sx={{ fontWeight: 500 }}>{option.name}</Box>
            {option.nodeTypeName && (
              <Box sx={{ fontSize: '12px', color: 'text.secondary' }}>
                {displayNodeTypeName(option.nodeTypeName)}
              </Box>
            )}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={isRequired}
          placeholder="Search..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={18} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
